const INVOICE_TASK_STATE_FIELDS = ["checked", "taskTime", "identityId", "taskTimeEnteredAt"];

const hasOwn = (obj, field) => (
	obj && Object.prototype.hasOwnProperty.call(obj, field)
);

const toTrimmedString = (value) => (
	value === undefined || value === null ? "" : String(value).trim()
);

export const getIndexedInvoiceTaskProgress = (source = {}) => {
	const safeSource = source && typeof source === "object" ? source : {};
	if (safeSource.completedInvoiceItemCount === undefined || safeSource.completedInvoiceItemCount === null
		|| safeSource.invoiceItemCount === undefined || safeSource.invoiceItemCount === null) {
		return null;
	}
	const rawCompleted = Number(safeSource.completedInvoiceItemCount);
	const rawTotal = Number(safeSource.invoiceItemCount);
	if (!Number.isFinite(rawCompleted) || !Number.isFinite(rawTotal) || rawTotal <= 0) {
		return null;
	}

	const total = Math.trunc(rawTotal);
	if (total <= 0) {
		return null;
	}
	const completed = Math.min(total, Math.max(0, Math.trunc(rawCompleted)));
	const percent = Math.round((completed / total) * 100);

	return {
		completed,
		total,
		percent,
		isComplete: completed === total,
		label: `Tasks ${completed} of ${total}`,
		ariaLabel: `${completed} of ${total} tasks completed`
	};
};

const getLineId = (item = {}) => toTrimmedString(item.lineId);

const getFallbackKeys = (item = {}) => {
	const id = toTrimmedString(item.id);
	const productId = toTrimmedString(item.productId);
	const description = toTrimmedString(item.description);
	const settlesContractLineId = toTrimmedString(item.settlesContractLineId);
	const keys = [];

	if (id && description) {
		keys.push(`id:${id}|description:${description}|settles:${settlesContractLineId}`);
	}
	if (productId && description) {
		keys.push(`productId:${productId}|description:${description}|settles:${settlesContractLineId}`);
	}

	return keys;
};

export const hasInvoiceTaskState = (item = {}) => (
	INVOICE_TASK_STATE_FIELDS.some((field) => hasOwn(item, field))
);

const getInvoiceTaskState = (item = {}) => {
	const taskState = {};

	INVOICE_TASK_STATE_FIELDS.forEach((field) => {
		if (hasOwn(item, field)) {
			taskState[field] = item[field];
		}
	});
	if (taskState.checked === false) {
		taskState.taskTime = 0;
		taskState.identityId = "";
		taskState.taskTimeEnteredAt = "";
	} else if ((Number(taskState.taskTime) || 0) <= 0) {
		taskState.taskTimeEnteredAt = "";
	}

	return taskState;
};

const buildInvoiceTaskStateLookup = (sourceItems = []) => {
	const lineIdTaskState = new Map();
	const fallbackCandidates = new Map();

	(Array.isArray(sourceItems) ? sourceItems : []).forEach((item) => {
		if (!item || typeof item !== "object" || !hasInvoiceTaskState(item)) {
			return;
		}

		const taskState = getInvoiceTaskState(item);
		const lineId = getLineId(item);

		if (lineId) {
			lineIdTaskState.set(lineId, taskState);
		}

		getFallbackKeys(item).forEach((key) => {
			const candidates = fallbackCandidates.get(key) || [];
			candidates.push(taskState);
			fallbackCandidates.set(key, candidates);
		});
	});

	const fallbackTaskState = new Map();
	fallbackCandidates.forEach((candidates, key) => {
		if (candidates.length === 1) {
			fallbackTaskState.set(key, candidates[0]);
		}
	});

	return {
		lineIdTaskState,
		fallbackTaskState
	};
};

const buildInvoiceTaskItemLookup = (sourceItems = []) => {
	const lineIdItems = new Map();
	const fallbackCandidates = new Map();

	(Array.isArray(sourceItems) ? sourceItems : []).forEach((item) => {
		if (!item || typeof item !== "object") {
			return;
		}

		const lineId = getLineId(item);
		if (lineId) {
			lineIdItems.set(lineId, item);
		}

		getFallbackKeys(item).forEach((key) => {
			const candidates = fallbackCandidates.get(key) || [];
			candidates.push(item);
			fallbackCandidates.set(key, candidates);
		});
	});

	const fallbackItems = new Map();
	fallbackCandidates.forEach((candidates, key) => {
		if (candidates.length === 1) {
			fallbackItems.set(key, candidates[0]);
		}
	});

	return {
		lineIdItems,
		fallbackItems
	};
};

const findMatchingInvoiceTaskItem = (item = {}, lookup = {}) => {
	const lineId = getLineId(item);
	if (lineId && lookup.lineIdItems && lookup.lineIdItems.has(lineId)) {
		return lookup.lineIdItems.get(lineId);
	}

	const fallbackKey = getFallbackKeys(item).find((key) => (
		lookup.fallbackItems && lookup.fallbackItems.has(key)
	));
	return fallbackKey ? lookup.fallbackItems.get(fallbackKey) : null;
};

const getComparableInvoiceTaskState = (item = {}) => JSON.stringify(getInvoiceTaskState(item));

export const getChangedInvoiceTaskItems = (previousItems = [], nextItems = []) => {
	const previousLookup = buildInvoiceTaskItemLookup(previousItems);

	return (Array.isArray(nextItems) ? nextItems : []).filter((item) => {
		if (!item || typeof item !== "object" || !hasInvoiceTaskState(item)) {
			return false;
		}

		const previousItem = findMatchingInvoiceTaskItem(item, previousLookup);
		return !previousItem
			|| getComparableInvoiceTaskState(previousItem) !== getComparableInvoiceTaskState(item);
	});
};

export const mergeInvoiceItemTaskState = (invoiceItems = [], sourceItems = []) => {
	if (!Array.isArray(invoiceItems)) {
		return invoiceItems;
	}

	const { lineIdTaskState, fallbackTaskState } = buildInvoiceTaskStateLookup(sourceItems);

	if (lineIdTaskState.size === 0 && fallbackTaskState.size === 0) {
		return invoiceItems;
	}

	return invoiceItems.map((item) => {
		if (!item || typeof item !== "object") {
			return item;
		}

		const lineId = getLineId(item);
		let taskState = lineId ? lineIdTaskState.get(lineId) : null;

		if (!taskState) {
			const fallbackKey = getFallbackKeys(item).find((key) => fallbackTaskState.has(key));
			taskState = fallbackKey ? fallbackTaskState.get(fallbackKey) : null;
		}

		return taskState ? { ...item, ...taskState } : item;
	});
};

const getEstimateValue = (estimate = {}) => toTrimmedString(estimate.value);

const cloneInvoiceItems = (invoiceItems = []) => (
	(Array.isArray(invoiceItems) ? invoiceItems : []).map((item) => (
		item && typeof item === "object" ? { ...item } : item
	))
);

const hasNonEmptyPricingJobDetails = (jobDetails = null) => (
	jobDetails && typeof jobDetails === "object"
		? ["name", "description", "instructions", "notes", "parts"].some((key) => toTrimmedString(jobDetails[key]) !== "")
		: false
);

export const mergeActiveEstimateSnapshot = ({
	estimates = [],
	selectedValue = "",
	invoiceItems = [],
	jobDetails = null
} = {}) => {
	if (!Array.isArray(estimates)) {
		return [];
	}

	const normalizedSelectedValue = toTrimmedString(selectedValue);
	const hasInvoiceItems = Array.isArray(invoiceItems) && invoiceItems.length > 0;
	const hasJobDetails = hasNonEmptyPricingJobDetails(jobDetails);
	if (!normalizedSelectedValue || (!hasInvoiceItems && !hasJobDetails)) {
		return estimates;
	}

	return estimates.map((estimate) => {
		if (!estimate || typeof estimate !== "object" || getEstimateValue(estimate) !== normalizedSelectedValue) {
			return estimate;
		}

		const nextEstimate = { ...estimate };
		if (hasInvoiceItems) {
			nextEstimate.invoiceItems = cloneInvoiceItems(invoiceItems);
		}
		if (hasJobDetails) {
			nextEstimate.jobDetails = { ...jobDetails };
			const jobName = toTrimmedString(jobDetails.name);
			if (jobName) {
				nextEstimate.name = jobName;
			}
		}
		return nextEstimate;
	});
};

const getEstimateInvoiceItems = (pricingState = {}, estimateValue = "") => {
	const estimates = Array.isArray(pricingState.estimates) ? pricingState.estimates : [];
	const estimate = estimates.find((entry) => getEstimateValue(entry) === estimateValue);
	return Array.isArray(estimate?.invoiceItems) ? estimate.invoiceItems : [];
};

const getDocumentInvoiceItems = (pricingContractProto = null, documentId = "") => {
	const documents = Array.isArray(pricingContractProto?.documents) ? pricingContractProto.documents : [];
	const document = documents.find((entry) => toTrimmedString(entry?.id) === documentId);
	return Array.isArray(document?.invoiceItems) ? document.invoiceItems : [];
};

const getActiveDocumentInvoiceItems = (pricingContractProto = null) => {
	const documents = Array.isArray(pricingContractProto?.documents) ? pricingContractProto.documents : [];
	const activeDocumentId = toTrimmedString(pricingContractProto?.activeDocumentId);
	const activeDocument = documents.find((entry) => toTrimmedString(entry?.id) === activeDocumentId)
		|| documents[documents.length - 1];
	return Array.isArray(activeDocument?.invoiceItems) ? activeDocument.invoiceItems : [];
};

export const resolveFullestActiveInvoiceItems = (pricingState = {}, fallbackItems = []) => {
	const selectedValue = toTrimmedString(pricingState.selectedValue);
	const candidates = [
		getActiveDocumentInvoiceItems(pricingState.pricingContractProto),
		getEstimateInvoiceItems(pricingState, selectedValue),
		Array.isArray(pricingState.invoiceItems) ? pricingState.invoiceItems : [],
		Array.isArray(fallbackItems) ? fallbackItems : []
	];

	return candidates.reduce((bestItems, candidateItems) => (
		Array.isArray(candidateItems) && candidateItems.length > bestItems.length
			? candidateItems
			: bestItems
	), []);
};

export const mergePricingInvoiceTaskState = (pricingState = {}, sourcePricingState = {}) => {
	const result = { ...(pricingState || {}) };
	const selectedValue = toTrimmedString(sourcePricingState.selectedValue) || toTrimmedString(pricingState.selectedValue);
	const sourceInvoiceItems = Array.isArray(sourcePricingState.invoiceItems) ? sourcePricingState.invoiceItems : [];

	if (Array.isArray(result.invoiceItems)) {
		result.invoiceItems = mergeInvoiceItemTaskState(result.invoiceItems, sourceInvoiceItems);
	}

	if (Array.isArray(result.estimates)) {
		result.estimates = result.estimates.map((estimate) => {
			if (!estimate || typeof estimate !== "object") {
				return estimate;
			}

			const estimateValue = getEstimateValue(estimate);
			const sourceItems = getEstimateInvoiceItems(sourcePricingState, estimateValue);
			const selectedSourceItems = estimateValue && estimateValue === selectedValue
				? [...sourceItems, ...sourceInvoiceItems]
				: sourceItems;

			return {
				...estimate,
				invoiceItems: mergeInvoiceItemTaskState(estimate.invoiceItems || [], selectedSourceItems)
			};
		});
	}

	if (result.pricingContractProto && Array.isArray(result.pricingContractProto.documents)) {
		const activeDocumentId = toTrimmedString(result.pricingContractProto.activeDocumentId);

		result.pricingContractProto = {
			...result.pricingContractProto,
			documents: result.pricingContractProto.documents.map((document) => {
				// Finalized agreements are immutable snapshots. Live task updates
				// belong to working invoice rows and draft documents only.
				if (!document || typeof document !== "object" || document.status === "locked") {
					return document;
				}

				const documentId = toTrimmedString(document.id);
				const sourceItems = getDocumentInvoiceItems(sourcePricingState.pricingContractProto, documentId);
				const activeSourceItems = documentId && documentId === activeDocumentId
					? [...sourceItems, ...sourceInvoiceItems]
					: sourceItems;

				return {
					...document,
					invoiceItems: mergeInvoiceItemTaskState(document.invoiceItems || [], activeSourceItems)
				};
			})
		};
	}

	return result;
};

const getEstimateValueFromSource = (source = {}) => {
	const explicitValue = toTrimmedString(source && source.selectedValue);
	if (explicitValue) {
		return explicitValue;
	}

	const estimates = Array.isArray(source && source.estimates) ? source.estimates : [];
	const originalEstimate = estimates.find((estimate) => getEstimateValue(estimate).toLowerCase() === "original");
	if (originalEstimate) {
		return getEstimateValue(originalEstimate);
	}
	if (estimates.length === 1) {
		return getEstimateValue(estimates[0]);
	}
	if (Array.isArray(source && source.invoiceItems) && source.invoiceItems.length > 0) {
		return "original";
	}

	return "";
};

export const hasSelectedEstimateChanged = (previousSource = {}, latestSource = {}) => (
	getEstimateValueFromSource(previousSource) !== getEstimateValueFromSource(latestSource)
);

export const mergeChecklistInvoiceTaskChanges = (
	latestChecklist = {},
	previousItems = [],
	nextItems = []
) => {
	const sourceChecklist = latestChecklist && typeof latestChecklist === "object"
		? latestChecklist
		: {};
	const changedItems = getChangedInvoiceTaskItems(previousItems, nextItems);
	const selectedValue = getEstimateValueFromSource(sourceChecklist);
	const latestPricingState = {
		selectedValue,
		invoiceItems: sourceChecklist.invoiceItems,
		estimates: sourceChecklist.estimates,
		pricingContractProto: sourceChecklist.pricingContractProto
	};
	const nextPricingState = mergePricingInvoiceTaskState(latestPricingState, {
		// The latest saved selection is canonical. A caller should reject a stale
		// selection before invoking this merge; using it here also prevents stale
		// Chat state from redirecting task updates to a different estimate.
		selectedValue,
		invoiceItems: changedItems
	});
	const nextChecklist = {
		...sourceChecklist
	};

	if (Array.isArray(sourceChecklist.invoiceItems)) {
		nextChecklist.invoiceItems = nextPricingState.invoiceItems;
	}
	if (Array.isArray(sourceChecklist.estimates)) {
		nextChecklist.estimates = nextPricingState.estimates;
	}
	if (sourceChecklist.pricingContractProto && typeof sourceChecklist.pricingContractProto === "object") {
		nextChecklist.pricingContractProto = nextPricingState.pricingContractProto;
	}

	return nextChecklist;
};
